import { createReducer, on } from '@ngrx/store';
import { ITestFixturesState } from './test-fixtures.interface';
import { TestFixturesActions } from './test-fixtures.actions';

export const testFixturesFeatureKey = 'testFixtures';

export type IState = ITestFixturesState;

export const initialState: IState = {
	scenarios: [],
	scenariosLoading: false,
	scenarioFilter: {},

	runs: [],
	runsPagination: { limit: 25, offset: 1, count: 0, totalPages: 0 },
	runsLoading: false,

	runningScenarioId: null,

	deletingRunId: null,
	purging: false,

	jobRunning: false,
};

export const reducer = createReducer(
	initialState,

	// ── Scenarios ──

	on(
		TestFixturesActions.loadScenarios,
		(state): IState => ({
			...state,
			scenariosLoading: true,
			scenariosError: undefined,
		}),
	),
	on(
		TestFixturesActions.loadScenariosSuccess,
		(state, action): IState => ({
			...state,
			scenariosLoading: false,
			scenarios: action.scenarios,
		}),
	),
	on(
		TestFixturesActions.loadScenariosError,
		(state, action): IState => ({
			...state,
			scenariosLoading: false,
			scenariosError: action.error,
		}),
	),
	on(
		TestFixturesActions.setScenarioFilter,
		(state, action): IState => ({
			...state,
			scenarioFilter: { ...state.scenarioFilter, ...action.filter },
		}),
	),
	on(
		TestFixturesActions.runScenario,
		(state, action): IState => ({
			...state,
			runningScenarioId: action.scenarioId,
			runError: undefined,
		}),
	),
	on(
		TestFixturesActions.runScenarioSuccess,
		(state, action): IState => ({
			...state,
			runningScenarioId: null,
			runResult: action.result,
		}),
	),
	on(
		TestFixturesActions.runScenarioError,
		(state, action): IState => ({
			...state,
			runningScenarioId: null,
			runError: action.error,
		}),
	),
	on(
		TestFixturesActions.clearRunResult,
		(state): IState => ({ ...state, runResult: undefined }),
	),

	// ── Runs ──

	on(
		TestFixturesActions.loadRuns,
		(state): IState => ({ ...state, runsLoading: true, runsError: undefined }),
	),
	on(
		TestFixturesActions.loadRunsSuccess,
		(state, action): IState => ({
			...state,
			runsLoading: false,
			runs: action.runs,
			runsPagination: action.pagination,
		}),
	),
	on(
		TestFixturesActions.loadRunsError,
		(state, action): IState => ({
			...state,
			runsLoading: false,
			runsError: action.error,
		}),
	),
	on(
		TestFixturesActions.setRunsOffset,
		(state, action): IState => ({
			...state,
			runsPagination: { ...state.runsPagination, offset: action.offset },
		}),
	),
	on(
		TestFixturesActions.setRunsLimit,
		(state, action): IState => ({
			...state,
			runsPagination: {
				...state.runsPagination,
				offset: 1,
				limit: action.limit,
			},
		}),
	),
	on(
		TestFixturesActions.deleteRun,
		(state, action): IState => ({
			...state,
			deletingRunId: action.runId,
			runsError: undefined,
		}),
	),
	on(
		TestFixturesActions.deleteRunSuccess,
		(state, action): IState => ({
			...state,
			deletingRunId: null,
			deleteResult: action.result,
		}),
	),
	on(
		TestFixturesActions.deleteRunError,
		(state, action): IState => ({
			...state,
			deletingRunId: null,
			runsError: action.error,
		}),
	),
	on(
		TestFixturesActions.purgeRuns,
		(state): IState => ({ ...state, purging: true, runsError: undefined }),
	),
	on(
		TestFixturesActions.purgeRunsSuccess,
		(state, action): IState => ({
			...state,
			purging: false,
			purgeResult: action.result,
		}),
	),
	on(
		TestFixturesActions.purgeRunsError,
		(state, action): IState => ({
			...state,
			purging: false,
			runsError: action.error,
		}),
	),

	// ── Jobs ──

	on(
		TestFixturesActions.runJob,
		(state): IState => ({ ...state, jobRunning: true, jobError: undefined }),
	),
	on(
		TestFixturesActions.runJobSuccess,
		(state, action): IState => ({
			...state,
			jobRunning: false,
			jobResult: action.result,
		}),
	),
	on(
		TestFixturesActions.runJobError,
		(state, action): IState => ({
			...state,
			jobRunning: false,
			jobError: action.error,
		}),
	),
	on(
		TestFixturesActions.clearJobResult,
		(state): IState => ({ ...state, jobResult: undefined }),
	),

	// ── Shared ──

	on(
		TestFixturesActions.clearError,
		(state): IState => ({
			...state,
			scenariosError: undefined,
			runsError: undefined,
			runError: undefined,
			jobError: undefined,
		}),
	),
);
